import { useEffect, useRef } from 'react';
import { useSession } from '@descope/react-sdk';
import { usePostHog } from 'posthog-js/react';

import { useGetCurrentUser } from '@/api/generated';

/**
 * Identifies the current authenticated user in PostHog.
 * This hook fetches the current user and calls posthog.identify() with their data.
 *
 * It sets the following properties in PostHog:
 * - email
 * - name (firstName + lastName)
 * - firstName
 * - lastName
 * - isMidotEmployee
 *
 * When the user logs out, it resets PostHog to clear the identified user.
 */
export const usePostHogIdentify = () => {
  const posthog = usePostHog();
  const { isAuthenticated, isSessionLoading } = useSession();
  const { data: currentUser } = useGetCurrentUser({
    query: {
      enabled: isAuthenticated && !isSessionLoading
    }
  });
  const identifiedUserIdRef = useRef<string | null>(null);
  const wasAuthenticatedRef = useRef<boolean>(false);

  // Reset PostHog when user logs out
  useEffect(
    function resetPostHogOnLogout() {
      if (!posthog || isSessionLoading) {
        return;
      }

      // User just logged out - reset PostHog
      if (wasAuthenticatedRef.current && !isAuthenticated) {
        posthog.reset();
        identifiedUserIdRef.current = null;
      }

      wasAuthenticatedRef.current = isAuthenticated;
    },
    [posthog, isAuthenticated, isSessionLoading]
  );

  // Identify user when authenticated and user data is available
  useEffect(
    function identifyUserInPostHog() {
      if (!posthog || !currentUser?.id) {
        return;
      }

      // Avoid re-identifying the same user
      if (identifiedUserIdRef.current === currentUser.id) {
        return;
      }

      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || undefined;

      posthog.identify(currentUser.id, {
        email: currentUser.email,
        name: fullName,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        isMidotEmployee: currentUser.isMidotEmployee
      });

      identifiedUserIdRef.current = currentUser.id;
    },
    [posthog, currentUser]
  );
};
